import { useMemo } from 'react';
import { IconButton } from 'src/components/Buttons';
import {
  EventItemHeader,
  EventItemHeaderType,
} from 'src/components/Events/EventItemHeader/EventItemHeader';
import { MetricsDisplay } from 'src/components/Metrics';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { CopyIcon } from 'src/svgs';
import { IconsFill, MoreIcon, PlusIcon } from 'src/svgs/Viagogo';
import { PurchaseDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { createPurchaseEventPageUrl } from 'src/utils/purchaseUtils';
import { Event, Performer, Venue } from 'src/WebApiController';

import * as styles from './PurchaseEventItemHeader.css';

export type PurchaseEventItemHeaderProps = Pick<
  EventItemHeaderType,
  'event' | 'performer' | 'venue' | 'hideYearFromDate'
> & {
  cost?: string;
  numOfTicketGroups?: number;
  numOfTickets?: number;
  disabled?: boolean;
  onAddTicketsForEvent?: (
    event: Event,
    performer?: Performer | null,
    venue?: Venue | null
  ) => void;
  /** Copy all of this event's ticket groups to newly-selected event(s). */
  onCopyTicketsForEvent?: (
    event: Event,
    performer?: Performer | null,
    venue?: Venue | null
  ) => void;
  removeMetrics?: boolean;
  showEventStatus?: boolean;
};

export const PurchaseEventItemHeader = ({
  event,
  performer,
  venue,
  numOfTicketGroups,
  numOfTickets,
  onAddTicketsForEvent,
  onCopyTicketsForEvent,
  cost,
  disabled,
  removeMetrics,
  ...rest
}: PurchaseEventItemHeaderProps) => {
  const purchaseEventUrl = useMemo(() => {
    const eventId = event?.virtId;
    if (eventId) {
      return createPurchaseEventPageUrl(eventId, [PurchaseDeeplinkQueryParam]);
    }
    return undefined;
  }, [event]);

  const actions =
    !disabled && (onAddTicketsForEvent || onCopyTicketsForEvent)
      ? [
          <PosDropdown
            key="EventActions"
            align="end"
            trigger={
              <IconButton
                icon={
                  <MoreIcon
                    fill={IconsFill.textBrand}
                    align="middle"
                    size={vars.iconSize.m}
                  />
                }
                titleContentId={ContentId.Actions}
              />
            }
          >
            {onAddTicketsForEvent && (
              <PosDropdownItem
                key="AddTicketsForEvent"
                onClick={() => onAddTicketsForEvent(event, performer, venue)}
              >
                <Stack alignItems="center" gap="m">
                  <PlusIcon
                    fill={IconsFill.currentColor}
                    size={vars.iconSize.m}
                  />
                  <Content id={ContentId.AddTicketsForThisEvent} />
                </Stack>
              </PosDropdownItem>
            )}
            {onCopyTicketsForEvent && (
              <PosDropdownItem
                key="CopyTicketsForEvent"
                onClick={() => onCopyTicketsForEvent(event, performer, venue)}
              >
                <Stack alignItems="center" gap="m">
                  <CopyIcon
                    fill={vars.color.textPrimary}
                    size={vars.iconSize.m}
                  />
                  <Content id={ContentId.CopyTickets} />
                </Stack>
              </PosDropdownItem>
            )}
          </PosDropdown>,
        ]
      : undefined;
  return (
    <EventItemHeader
      showYear
      event={event}
      performer={performer}
      venue={venue}
      eventNameLink={purchaseEventUrl}
      {...rest}
      actions={actions}
      metrics={
        !removeMetrics && (
          <MetricsDisplay
            key="metrics"
            mainMetrics={cost}
            subMetricBelow
            subMetrics={
              <div className={styles.purchaseTicketCountsContainer}>
                {numOfTicketGroups ? (
                  <div>
                    {numOfTicketGroups}{' '}
                    <Content
                      id={
                        numOfTicketGroups > 1
                          ? ContentId.TicketGroups
                          : ContentId.TicketGroup
                      }
                    />
                  </div>
                ) : null}
                {numOfTickets ? (
                  <div>
                    {numOfTickets}{' '}
                    <Content
                      id={
                        numOfTickets > 1 ? ContentId.Tickets : ContentId.Ticket
                      }
                    />
                  </div>
                ) : null}
              </div>
            }
          />
        )
      }
    />
  );
};
